import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiService } from '@/services/api';
import { useTranslation } from 'react-i18next';

type Stage = 'choose' | 'loading' | 'accepted' | 'rejected' | 'error';

const IntentAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('choose');
  const [message, setMessage] = useState('');

  const handleAccept = async () => {
    if (!token) return;
    setStage('loading');
    try {
      await apiService.interviewIntents.accept(token);
      setStage('accepted');
    } catch (err: any) {
      setMessage(err?.response?.data?.message || t('intentAccept.invalidLink', '連結無效或已過期。'));
      setStage('error');
    }
  };

  const handleReject = async () => {
    if (!token) return;
    setStage('loading');
    try {
      await apiService.interviewIntents.reject(token);
      setStage('rejected');
    } catch (err: any) {
      setMessage(err?.response?.data?.message || t('intentAccept.invalidLink', '連結無效或已過期。'));
      setStage('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">

        {stage === 'choose' && (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 mx-auto">
              <MessageCircle className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('intentAccept.title', '面試意向')}</h1>
              <p className="mt-2 text-muted-foreground">
                {t('intentAccept.description', '一位僱主對您的資料有興趣，希望安排面試。請選擇您的回覆：')}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button className="w-full gap-2" onClick={handleAccept}>
                <CheckCircle className="h-4 w-4" />
                {t('intentAccept.accept', '確認有興趣，接受邀請')}
              </Button>
              <Button variant="outline" className="w-full gap-2 text-muted-foreground" onClick={handleReject}>
                <XCircle className="h-4 w-4" />
                {t('intentAccept.reject', '婉拒此次邀請')}
              </Button>
            </div>
          </>
        )}

        {stage === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">{t('common.processing', '處理中...')}</p>
          </>
        )}

        {stage === 'accepted' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">{t('intentAccept.acceptedTitle', '已確認面試意向！')}</h1>
            <p className="text-muted-foreground">{t('intentAccept.acceptedDesc', '僱主將收到通知並與您聯繫。感謝您的回覆。')}</p>
            <Button asChild>
              <Link to="/">{t('intentAccept.backHome', '返回首頁')}</Link>
            </Button>
          </>
        )}

        {stage === 'rejected' && (
          <>
            <XCircle className="h-16 w-16 text-muted-foreground mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">{t('intentAccept.rejectedTitle', '已婉拒邀請')}</h1>
            <p className="text-muted-foreground">{t('intentAccept.rejectedDesc', '感謝您的回覆。如有其他機會，我們將再次通知您。')}</p>
            <Button variant="outline" asChild>
              <Link to="/">{t('intentAccept.backHome', '返回首頁')}</Link>
            </Button>
          </>
        )}

        {stage === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">{t('intentAccept.errorTitle', '出現問題')}</h1>
            <p className="text-muted-foreground">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/">{t('intentAccept.backHome', '返回首頁')}</Link>
            </Button>
          </>
        )}

      </div>
    </div>
  );
};

export default IntentAcceptPage;
